import React, { useState, useEffect } from 'react';
import { User, Lock, Save, Loader, Eye, EyeOff, AlertTriangle } from 'lucide-react';
import { usePageTitle } from '../../hooks/usePageTitle';
import api from '../../services/api';
import { LoadingState } from '../../components/LoadingSpinner';
import toast from 'react-hot-toast';

interface UserProfile {
  id: string;
  name: string;
  email: string;
  role: string;
  company_id: string;
  company_name: string;
  company_slug: string;
  created_at: string;
}

const inputBase =
  'w-full bg-white border border-tinta/15 rounded-lg px-4 py-2.5 text-sm text-tinta placeholder:text-tinta-soft/70 placeholder:italic focus:outline-none focus:ring-2 focus:ring-bordo/40 focus:border-bordo transition-colors';

const labelBase =
  'block text-xs font-bold uppercase tracking-wider text-tinta mb-1.5';

const UserProfilePage: React.FC = () => {
  usePageTitle('Meu Perfil');
  const [loading, setLoading] = useState(true);
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);

  // Profile form
  const [profileForm, setProfileForm] = useState({
    name: '',
  });

  // Password form
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  });

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get<UserProfile>('/user/profile');
      setProfile(response.data);
      setProfileForm({
        name: response.data.name,
      });
    } catch (err: any) {
      console.error('Error fetching profile:', err);
      setError(err.response?.data?.error || 'Erro ao carregar perfil');
      toast.error('Erro ao carregar perfil');
    } finally {
      setLoading(false);
    }
  };

  const handleProfileSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!profileForm.name.trim()) {
      toast.error('Nome é obrigatório');
      return;
    }

    try {
      setSavingProfile(true);
      const response = await api.put('/user/profile', {
        name: profileForm.name.trim(),
      });

      setProfile(prev => prev ? { ...prev, name: response.data.user.name } : null);
      toast.success('Perfil atualizado com sucesso!');
    } catch (err: any) {
      console.error('Error updating profile:', err);
      toast.error(err.response?.data?.error || 'Erro ao atualizar perfil');
    } finally {
      setSavingProfile(false);
    }
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!passwordForm.currentPassword) {
      toast.error('Senha atual é obrigatória');
      return;
    }

    if (!passwordForm.newPassword) {
      toast.error('Nova senha é obrigatória');
      return;
    }

    if (passwordForm.newPassword.length < 6) {
      toast.error('Nova senha deve ter pelo menos 6 caracteres');
      return;
    }

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error('As senhas não coincidem');
      return;
    }

    try {
      setSavingPassword(true);
      await api.put('/user/password', {
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword,
      });

      // Clear form
      setPasswordForm({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });

      toast.success('Senha alterada com sucesso!');
    } catch (err: any) {
      console.error('Error changing password:', err);
      toast.error(err.response?.data?.error || 'Erro ao alterar senha');
    } finally {
      setSavingPassword(false);
    }
  };

  const handleProfileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setProfileForm(prev => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPasswordForm(prev => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const togglePasswordVisibility = (field: 'current' | 'new' | 'confirm') => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    });
  };

  const getRoleLabel = (role: string) => {
    const roles: Record<string, string> = {
      owner: 'Proprietário',
      admin: 'Administrador',
      member: 'Membro',
    };
    return roles[role] || role;
  };

  if (loading) {
    return <LoadingState message="Carregando perfil..." />;
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="flex gap-3 p-4 rounded-lg bg-bordo/8 border border-bordo/30">
          <AlertTriangle className="w-5 h-5 text-bordo mt-0.5 flex-shrink-0" />
          <p className="text-sm text-tinta leading-relaxed">{error}</p>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="flex gap-3 p-4 rounded-lg bg-bordo/8 border border-bordo/30">
          <AlertTriangle className="w-5 h-5 text-bordo mt-0.5 flex-shrink-0" />
          <p className="text-sm text-tinta leading-relaxed">Perfil não encontrado</p>
        </div>
      </div>
    );
  }

  const confirmMismatch =
    !!passwordForm.confirmPassword &&
    passwordForm.newPassword !== passwordForm.confirmPassword;

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div>
        <h1 className="font-display text-3xl font-black text-tinta leading-tight">
          Meu <span className="italic text-bordo">perfil</span>
        </h1>
        <p className="mt-2 text-sm text-tinta-soft">Gerencie suas informações pessoais</p>
      </div>

      {/* Profile Information */}
      <div className="bg-white rounded-xl shadow-sm border border-tinta/10 p-6">
        <h2 className="font-display text-lg font-bold text-tinta mb-4 flex items-center gap-2">
          <User size={20} className="text-bordo" />
          Informações Pessoais
        </h2>

        <form onSubmit={handleProfileSubmit} className="space-y-4">
          {/* Name */}
          <div>
            <label htmlFor="name" className={labelBase}>
              Nome Completo *
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={profileForm.name}
              onChange={handleProfileChange}
              required
              maxLength={255}
              className={inputBase}
              placeholder="Seu nome completo"
            />
          </div>

          {/* Email (read-only) */}
          <div>
            <label htmlFor="email" className={labelBase}>
              Email
            </label>
            <input
              type="email"
              id="email"
              value={profile.email}
              disabled
              className={`${inputBase} disabled:bg-creme-light disabled:cursor-not-allowed disabled:text-tinta-soft`}
            />
            <p className="text-xs text-tinta-soft italic mt-1">
              O email não pode ser alterado
            </p>
          </div>

          {/* Save Button */}
          <div className="flex justify-end pt-4">
            <button
              type="submit"
              disabled={savingProfile || profileForm.name === profile.name}
              className="px-6 py-2.5 rounded-lg text-sm font-bold text-creme bg-bordo hover:bg-bordo-dark disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
            >
              {savingProfile ? (
                <>
                  <Loader size={16} className="animate-spin" />
                  Salvando...
                </>
              ) : (
                <>
                  <Save size={16} />
                  Salvar Alterações
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      {/* Change Password */}
      <div className="bg-white rounded-xl shadow-sm border border-tinta/10 p-6">
        <h2 className="font-display text-lg font-bold text-tinta mb-4 flex items-center gap-2">
          <Lock size={20} className="text-bordo" />
          Alterar Senha
        </h2>

        <form onSubmit={handlePasswordSubmit} className="space-y-4">
          {/* Current Password */}
          <div>
            <label htmlFor="currentPassword" className={labelBase}>
              Senha Atual *
            </label>
            <div className="relative">
              <input
                type={showPasswords.current ? 'text' : 'password'}
                id="currentPassword"
                name="currentPassword"
                value={passwordForm.currentPassword}
                onChange={handlePasswordChange}
                required
                className={`${inputBase} pr-10`}
                placeholder="Digite sua senha atual"
              />
              <button
                type="button"
                onClick={() => togglePasswordVisibility('current')}
                className="absolute inset-y-0 right-0 flex items-center pr-3 text-tinta-soft hover:text-bordo transition-colors"
              >
                {showPasswords.current ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          {/* New Password */}
          <div>
            <label htmlFor="newPassword" className={labelBase}>
              Nova Senha *
            </label>
            <div className="relative">
              <input
                type={showPasswords.new ? 'text' : 'password'}
                id="newPassword"
                name="newPassword"
                value={passwordForm.newPassword}
                onChange={handlePasswordChange}
                required
                minLength={6}
                className={`${inputBase} pr-10`}
                placeholder="Mínimo 6 caracteres"
              />
              <button
                type="button"
                onClick={() => togglePasswordVisibility('new')}
                className="absolute inset-y-0 right-0 flex items-center pr-3 text-tinta-soft hover:text-bordo transition-colors"
              >
                {showPasswords.new ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          {/* Confirm Password */}
          <div>
            <label htmlFor="confirmPassword" className={labelBase}>
              Confirmar Nova Senha *
            </label>
            <div className="relative">
              <input
                type={showPasswords.confirm ? 'text' : 'password'}
                id="confirmPassword"
                name="confirmPassword"
                value={passwordForm.confirmPassword}
                onChange={handlePasswordChange}
                required
                minLength={6}
                className={`w-full bg-white border rounded-lg px-4 py-2.5 text-sm text-tinta placeholder:text-tinta-soft/70 placeholder:italic focus:outline-none focus:ring-2 focus:ring-bordo/40 focus:border-bordo transition-colors pr-10 ${
                  confirmMismatch ? 'border-bordo/60' : 'border-tinta/15'
                }`}
                placeholder="Digite novamente a nova senha"
              />
              <button
                type="button"
                onClick={() => togglePasswordVisibility('confirm')}
                className="absolute inset-y-0 right-0 flex items-center pr-3 text-tinta-soft hover:text-bordo transition-colors"
              >
                {showPasswords.confirm ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            {confirmMismatch && (
              <p className="text-xs text-bordo italic mt-1">As senhas não coincidem</p>
            )}
          </div>

          {/* Save Button */}
          <div className="flex justify-end pt-4">
            <button
              type="submit"
              disabled={savingPassword}
              className="px-6 py-2.5 rounded-lg text-sm font-bold text-creme bg-bordo hover:bg-bordo-dark disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
            >
              {savingPassword ? (
                <>
                  <Loader size={16} className="animate-spin" />
                  Alterando Senha...
                </>
              ) : (
                <>
                  <Lock size={16} />
                  Alterar Senha
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      {/* Account Information */}
      <div className="bg-white rounded-xl shadow-sm border border-tinta/10 p-6">
        <h2 className="font-display text-lg font-bold text-tinta mb-4">
          Informações da Conta
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <span className="text-xs font-bold uppercase tracking-wider text-tinta-soft/80">
              ID do Usuário
            </span>
            <p className="font-mono text-sm text-tinta mt-1">{profile.id}</p>
          </div>

          <div>
            <span className="text-xs font-bold uppercase tracking-wider text-tinta-soft/80">
              Função
            </span>
            <p className="text-sm text-tinta mt-1">{getRoleLabel(profile.role)}</p>
          </div>

          <div>
            <span className="text-xs font-bold uppercase tracking-wider text-tinta-soft/80">
              Empresa
            </span>
            <p className="text-sm text-tinta mt-1">{profile.company_name}</p>
          </div>

          <div>
            <span className="text-xs font-bold uppercase tracking-wider text-tinta-soft/80">
              Slug da Empresa
            </span>
            <p className="font-mono text-sm text-tinta mt-1">{profile.company_slug}</p>
          </div>

          <div className="md:col-span-2">
            <span className="text-xs font-bold uppercase tracking-wider text-tinta-soft/80">
              Membro desde
            </span>
            <p className="text-sm text-tinta mt-1">{formatDate(profile.created_at)}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserProfilePage;
